import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchJobPhases } from '@/redux/thunk/v2/jobPhasesThunk';
import { Phase as ApiPhase } from '@/api/v2/jobPhases';

// Types matching the API response structure
export interface Product {
  uuid: string;
  quote_id: string;
  section_id: string;
  product_id: string | null;
  supplier_id: string | null;
  item_type: number;
  item_code: string | null;
  manufacture_number: string | null;
  part_number: string | null;
  barcode: string | null;
  name: string;
  description: string;
  attachment_url: string | null;
  unit_cost: string;
  unit_price: string;
  quantity: number;
  total: string;
  display_order: number;
  markup: number;
  tax: number;
  discount: number;
}

export interface ColumnVisibility {
  product: boolean;
  qty: boolean;
  price: boolean;
  tax: boolean;
  discount: boolean;
  total: boolean;
}

export interface Section {
  uuid: string;
  title: string;
  description: string;
  section_type: number;
  display_order: number;
  is_active: boolean;
  is_approved: boolean;
  parent_section_id: string | null;
  column_visibility: ColumnVisibility;
  sections: Section[]; // Nested sections
  products: Product[];
  quote_labours: any[];
  quote_expenses: any[];
  quote_notes: any[];
  total_items: number;
  subtotal: number;
  cost: number;
  created_at: string;
  updated_at: string;
}

export interface Assigner {
  profile_pic: string;
  name: string;
  id: string;
}

export interface Task {
  id: string;
  desc: string;
  assigners: Assigner[];
}

export interface Phase {
  id: string;
  title: string;
  selected: boolean;
  is_invoiced: boolean;
  task: Task;
  sections: Section[];
}

export interface JobPhasesState {
  phases: Phase[];
  loading: boolean;
  error: string | null;
}

const initialState: JobPhasesState = {
  phases: [],
  loading: false,
  error: null,
};

const jobPhasesSlice = createSlice({
  name: 'v2/jobPhases',
  initialState,
  reducers: {
    // Select phase by ID and update selected property
    selectPhaseById: (state, action: PayloadAction<string>) => {
      const phaseId = action.payload;
      // Find and select the target phase
      console.log(state.phases, 'state.phases');
      const targetPhase = state.phases.find((phase) => phase.id === phaseId);
      if (targetPhase) {
        targetPhase.selected = !targetPhase.selected;
      }
    },

    // Clear phases data
    clearPhases: (state) => {
      state.phases = [];
      state.error = null;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Update section approval status
    updateSectionApproval: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        isApproved: boolean;
      }>,
    ) => {
      const { phaseId, sectionId, isApproved } = action.payload;
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        const updateSection = (sections: Section[]): boolean => {
          for (const section of sections) {
            if (section.uuid === sectionId) {
              section.is_approved = isApproved;
              return true;
            }
            if (
              section.sections.length > 0 &&
              updateSection(section.sections)
            ) {
              return true;
            }
          }
          return false;
        };
        updateSection(phase.sections);
      }
    },

    // Update section active status
    updateSectionActiveStatus: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        isActive: boolean;
      }>,
    ) => {
      const { phaseId, sectionId, isActive } = action.payload;
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        const updateSection = (sections: Section[]): boolean => {
          for (const section of sections) {
            if (section.uuid === sectionId) {
              section.is_active = isActive;
              return true;
            }
            if (
              section.sections.length > 0 &&
              updateSection(section.sections)
            ) {
              return true;
            }
          }
          return false;
        };
        updateSection(phase.sections);
      }
    },

    // Update section details (title and description)
    updateSectionDetails: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        title?: string;
        description?: string;
      }>,
    ) => {
      const { phaseId, sectionId, title, description } = action.payload;
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        const updateSection = (sections: Section[]): boolean => {
          for (const section of sections) {
            if (section.uuid === sectionId) {
              if (title !== undefined) section.title = title;
              if (description !== undefined) section.description = description;
              return true;
            }
            if (
              section.sections.length > 0 &&
              updateSection(section.sections)
            ) {
              return true;
            }
          }
          return false;
        };
        updateSection(phase.sections);
      }
    },

    // Update product/line item details
    updateLineItem: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        lineItemId: string;
        updates: Partial<Product>;
      }>,
    ) => {
      const { phaseId, sectionId, lineItemId, updates } = action.payload;
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        const updateSection = (sections: Section[]): boolean => {
          for (const section of sections) {
            if (section.uuid === sectionId) {
              const product = section.products.find(
                (p) => p.uuid === lineItemId,
              );
              if (product) {
                Object.assign(product, updates);

                // Recalculate total if price or quantity changed
                if ('unit_price' in updates || 'quantity' in updates) {
                  const unitPrice = parseFloat(String(product.unit_price || 0));
                  const quantity = product.quantity || 0;
                  product.total = String(unitPrice * quantity);
                }

                return true;
              }
            }
            if (
              section.sections.length > 0 &&
              updateSection(section.sections)
            ) {
              return true;
            }
          }
          return false;
        };
        updateSection(phase.sections);
      }
    },

    // Universal method to update any product field
    updateLineItemField: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        lineItemId: string;
        field: keyof Product;
        value: any;
      }>,
    ) => {
      const { phaseId, sectionId, lineItemId, field, value } = action.payload;
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        const updateSection = (sections: Section[]): boolean => {
          for (const section of sections) {
            if (section.uuid === sectionId) {
              const product = section.products.find(
                (p) => p.uuid === lineItemId,
              );
              if (product) {
                (product as any)[field] = value;

                // Recalculate total if price or quantity changed
                if (field === 'unit_price' || field === 'quantity') {
                  const unitPrice = parseFloat(String(product.unit_price || 0));
                  const quantity = product.quantity || 0;
                  product.total = String(unitPrice * quantity);
                }

                return true;
              }
            }
            if (
              section.sections.length > 0 &&
              updateSection(section.sections)
            ) {
              return true;
            }
          }
          return false;
        };
        updateSection(phase.sections);
      }
    },

    // Update column visibility
    updateColumnVisibility: (
      state,
      action: PayloadAction<{
        phaseId: string;
        sectionId: string;
        columnVisibility: Partial<ColumnVisibility>;
      }>,
    ) => {
      const { phaseId, sectionId, columnVisibility } = action.payload;
      const phase = state.phases.find((p) => p.id === phaseId);
      if (phase) {
        const updateSection = (sections: Section[]): boolean => {
          for (const section of sections) {
            if (section.uuid === sectionId) {
              section.column_visibility = {
                ...section.column_visibility,
                ...columnVisibility,
              };
              return true;
            }
            if (
              section.sections.length > 0 &&
              updateSection(section.sections)
            ) {
              return true;
            }
          }
          return false;
        };
        updateSection(phase.sections);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobPhases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobPhases.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.phases = action.payload.data.map((phase: ApiPhase) => ({
          ...phase,
          selected: false,
        }));
      })
      .addCase(fetchJobPhases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch job phases';
        console.error('Failed to fetch job phases:', action.payload);
      });
  },
});

export const {
  selectPhaseById,
  clearPhases,
  setLoading,
  setError,
  updateSectionApproval,
  updateSectionActiveStatus,
  updateSectionDetails,
  updateLineItem,
  updateLineItemField,
  updateColumnVisibility,
} = jobPhasesSlice.actions;

// Selectors
export const selectPhaseSectionColumnVisibility = (
  state: { jobPhasesV2: JobPhasesState },
  phaseId: string,
  sectionId: string,
): ColumnVisibility | null => {
  const phase = state.jobPhasesV2.phases.find((p) => p.id === phaseId);
  if (!phase) return null;

  const findSection = (sections: Section[]): Section | null => {
    for (const section of sections) {
      if (section.uuid === sectionId) {
        return section;
      }
      if (section.sections.length > 0) {
        const found = findSection(section.sections);
        if (found) return found;
      }
    }
    return null;
  };

  const section = findSection(phase.sections);
  return section?.column_visibility || null;
};

export const selectPhaseSectionById = (
  state: { jobPhasesV2: JobPhasesState },
  phaseId: string,
  sectionId: string,
): Section | null => {
  const phase = state.jobPhasesV2.phases.find((p) => p.id === phaseId);
  if (!phase) return null;

  const findSection = (sections: Section[]): Section | null => {
    for (const section of sections) {
      if (section.uuid === sectionId) {
        return section;
      }
      if (section.sections.length > 0) {
        const found = findSection(section.sections);
        if (found) return found;
      }
    }
    return null;
  };

  return findSection(phase.sections);
};

export default jobPhasesSlice.reducer;
